import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { createRoot } from 'react-dom/client';
import * as Yup from 'yup';
import submitQuoteWizard from './requestQuoteSubmitWizard';;
import countries from '../wizard/countries';
import resolveGclid from '../../universals/gclidAttribution';

const STEP1_OPTIONS = [
    { value: 'development', label: 'Development' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'succession', label: 'Succession' },
    { value: 'facilitation', label: 'Facilitation' },
];

const step1Schema = Yup.object({
    choices: Yup.array()
        .of(Yup.string().oneOf(STEP1_OPTIONS.map((o) => o.value)))
        .min(1, 'Select at least one option'),
});

const WORK_EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@(?!(?:gmail\.com|googlemail\.com|yahoo\.com|ymail\.com|rocketmail\.com|outlook\.com|hotmail\.com|live\.com|msn\.com|aol\.com|icloud\.com|me\.com|mac\.com|mail\.com|fastmail\.com|hey\.com|zoho\.com|protonmail\.com|att\.net|currently\.com|sbcglobal\.net|bellsouth\.net|pacbell\.net|flash\.net|ameritech\.net|prodigy\.net|snet\.net|swbell\.net|nvbell\.net|verizon\.net|comcast\.net|cox\.net|charter\.net|spectrum\.net|rr\.com|twc\.com|brighthouse\.com|optimum\.net|optonline\.net|suddenlink\.net|frontier\.com|frontiernet\.net|centurylink\.net|centurytel\.net|q\.com|embarqmail\.com|cableone\.net|windstream\.net|earthlink\.net|mindspring\.com|peoplepc\.com|juno\.com|netzero\.com)$)(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/i;

const step2Schema = Yup.object({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string()
        .trim()
        .email('Enter a valid email')
        .required('Email is required')
        .test('work-email-or-test-bypass', 'Please use a work email address', (value) => {
            const normalizedValue = (value ?? '').trim();
            if (!normalizedValue) return true;

            // Allow any address using a "+test" tag (e.g., joshua+test@gmail.com)
            const bypassWorkEmailCheck = /\+test(?=@)/i.test(normalizedValue);
            return bypassWorkEmailCheck || WORK_EMAIL_REGEX.test(normalizedValue);
        }),
    company: Yup.string().trim().required('Company name is required'),
    country: Yup.string()
        .oneOf(countries, 'Select a country')
        .required('Country is required'),
    comments: Yup.string().trim(), // optional
});

const STEP2_REQUIRED_FIELDS = [
    'firstName',
    'lastName',
    'email',
    'company',
    'country',
];

const initialValues = {
    // step 1
    choices: [],
    // step 2
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    country: '',
    comments: '',
};

function StepOne({ formik }) {
    const showChoicesError = !!(formik.touched.choices && formik.errors.choices);

    return (
        <>
            <p className="h3 font-weight-bold">Step 1:</p>
            <p className="h3 font-weight-bold">Please select your interest(s)</p>

            <legend className="h5 mb-3">Choose all that apply:</legend>

            {STEP1_OPTIONS.map((opt) => (
                <React.Fragment key={opt.value}>
                    <Field
                        type="checkbox"
                        name="choices"
                        value={opt.value}
                        id={`opt-${opt.value}`}
                        onBlur={() => formik.setFieldTouched('choices', true, true)}
                    />
                    <label className="type--header-xsm font-weight-bold" htmlFor={`opt-${opt.value}`}>
                        {opt.label}
                    </label>
                </React.Fragment>
            ))}

            {showChoicesError && (
                <div className="invalid-feedback d-block">{formik.errors.choices}</div>
            )}
        </>
    );
}

function StepTwo({ formik, attempted }) {
    const show = (meta) => attempted && !!meta.error;
    const [gclidString, setGclidString] = useState("");

    useEffect(() => {
        setGclidString(resolveGclid());
    }, []);
    return (
        <>
            <p className="h3 font-weight-bold">Step 2:</p>
            <legend className="h3 font-weight-bold mb-3">What's the best way to reach you?</legend>
            <Field name="firstName">
                {({ field, meta }) => (
                    <div className="demo__form-group">
                        <label htmlFor="firstName">First Name</label>
                        <input
                            id="firstName"
                            type="text"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid parsley-error' : ''}`}
                            placeholder="First Name"
                        />
                        {show(meta) && (
                            <div className="invalid-feedback">{meta.error}</div>
                        )}
                    </div>
                )}
            </Field>

            <Field name="lastName">
                {({ field, meta }) => (
                    <div className="demo__form-group">
                        <label htmlFor="lastName">Last Name</label>
                        <input
                            id="lastName"
                            type="text"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid parsley-error' : ''}`}
                            placeholder="Last Name"
                        />
                        {show(meta) && <div className="invalid-feedback">{meta.error}</div>}
                    </div>
                )}
            </Field>

            <Field name="email">
                {({ field, meta }) => (
                    <div className="demo__form-group">
                        <label htmlFor="email">Company Email</label>
                        <input
                            id="email"
                            type="email"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid parsley-error' : ''}`}
                            placeholder="name@company.com"
                        />
                        {show(meta) && <div className="invalid-feedback">{meta.error}</div>}
                    </div>
                )}
            </Field>

            <Field name="company">
                {({ field, meta }) => (
                    <div className="demo__form-group">
                        <label htmlFor="company">Company Name</label>
                        <input
                            id="company"
                            type="text"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid parsley-error' : ''}`}
                            placeholder="Company Name"
                        />
                        {show(meta) && <div className="invalid-feedback">{meta.error}</div>}
                    </div>
                )}
            </Field>

            <Field name="country">
                {({ field, meta }) => (
                    <div className="demo__form-group grid__col--span-2 w-100 pr-4">
                        <label htmlFor="country">Country</label>
                        <select
                            id="country"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid parsley-error' : ''}`}
                        >
                            <option value="">Select a Country/Region</option>
                            {countries.map((country) => (
                                <option value={country}>{country}</option>
                            ))}
                        </select>
                        {show(meta) && <div className="invalid-feedback">{meta.error}</div>}
                    </div>
                )}
            </Field>

            <Field name="comments">
                {({ field, meta }) => (
                    <div className="demo__form-group grid__col--span-2 w-100 pr-4">
                        <label htmlFor="comments">Comments (optional)</label>
                        <textarea
                            id="comments"
                            rows="3"
                            {...field}
                            className={`form-control${show(meta) ? ' is-invalid' : ''}`}
                            placeholder="Anything else you’d like us to know?"
                        />
                        {show(meta) && <div className="invalid-feedback">{meta.error}</div>}
                    </div>
                )}
            </Field>
            <input type="hidden" data-formfield="assessmentFormId" name="formId" value="1350" />
            <input type="hidden" data-formfield="utm_campaign" id="utm_campaign" name="utm_campaign" value="" />
            <input type="hidden" data-formfield="utm_source" id="utm_source" name="utm_source" value="" />
            <input type="hidden" data-formfield="utm_medium" id="utm_medium" name="utm_medium" value="" />
            <input type="hidden" data-formfield="utm_content" id="utm_content" name="utm_content" value="" />
            <input type="hidden" data-formfield="utm_id" name="utm_id" id="utm_id" value="" />
            <input type="hidden" data-formfield="gclid" id="gclid_field" name="gclid" value={gclidString} />
            <p className="demo__form__privacy type--paragraph-xsm">By clicking Submit below, you consent to allow DDI to store and process the personal information submitted above. See our <a href="https://www.ddi.com/privacy">privacy policy</a> for more details or to revoke your consent at any time.</p>

        </>
    );
}

async function handleNextFromStep1(formik, goNext) {
    // Mark group touched so the error can show if empty
    formik.setTouched({ ...formik.touched, choices: true }, true);

    const errors = await formik.validateForm();
    if (errors.choices) {
        document.querySelector('input[name="choices"]')?.focus();
        return;
    }
    goNext();
}

async function handleSubmitFromStep2(formik, setAttempted) {
    setAttempted(true);

    // Mark required fields touched so messages render after attempt
    const touchedUpdate = { ...formik.touched };
    STEP2_REQUIRED_FIELDS.forEach((f) => (touchedUpdate[f] = true));
    if (formik.values.comments) touchedUpdate.comments = true;
    formik.setTouched(touchedUpdate, true);

    const errors = await formik.validateForm();
    const order = [...STEP2_REQUIRED_FIELDS, 'comments'];
    const firstInvalid = order.find((f) => errors[f]);

    if (firstInvalid) {
        document.getElementById(firstInvalid)?.focus();
        return;
    }
    await formik.submitForm();
}

function GetQuoteWizard({
    onSubmit = (vals) => submitQuoteWizard(vals),
}) {
    const [step, setStep] = React.useState(0);
    const [step2TriedSubmit, setStep2TriedSubmit] = React.useState(false);
    const isLast = step === 1;

    return (
        <Formik
            initialValues={initialValues}
            validateOnMount
            validationSchema={() => (step === 0 ? step1Schema : step2Schema)}
            onSubmit={onSubmit}
        >
            {(formik) => {
                const hasAnyChoice = (formik.values.choices || []).length > 0;
                const isStep1Complete = hasAnyChoice;

                return (
                    <Form noValidate className="demo__form__data">
                        <div className="demo__form__data--step1 grid__col--span-2">
                            {step === 0 ? (
                                <StepOne formik={formik} />
                            ) : (
                                <StepTwo formik={formik} attempted={step2TriedSubmit} />
                            )}

                            <div className="d-flex justify-content-between w-100 mt-3">
                                {isLast &&
                                    <button
                                        type="button"
                                        className="btn__base btn__base--theme-drk-grey ml-0"
                                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                                        disabled={step === 0 || formik.isSubmitting}
                                    >
                                        Back
                                    </button>
                                }

                                {!isLast ? (
                                    <button
                                        type="button"
                                        className="btn__base btn__base--icon-r grid__col--span-2 ml-1"
                                        onClick={() =>
                                            handleNextFromStep1(formik, () => setStep(1))
                                        }
                                        disabled={formik.isSubmitting || !isStep1Complete}
                                    >
                                        Next
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn__base btn__base--icon-r grid__col--span-2 ml-1"
                                        disabled={formik.isSubmitting}
                                        onClick={() =>
                                            handleSubmitFromStep2(formik, setStep2TriedSubmit)
                                        }
                                    >
                                        {formik.isSubmitting ? 'Submitting form!' : 'Submit'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}

let quoteWizardContainer = document.createElement('div');
quoteWizardContainer.setAttribute('id', 'quoteWizardContainer');
document.querySelector('#form.demo__form__container').prepend(quoteWizardContainer);
const root = createRoot(quoteWizardContainer);
root.render(<GetQuoteWizard></GetQuoteWizard>);